import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

interface TransferOwnershipDialogProps {
  open: boolean;
  confirmation: string;
  children: React.ReactNode;
  onTransfer?: (email: string) => Promise<void>;
  onOpenChange?: (open: boolean) => void;
}

export default function TransferOwnershipDialog({
  open,
  confirmation,
  children,
  onTransfer,
  onOpenChange,
}: TransferOwnershipDialogProps) {
  const [email, setEmail] = useState("");
  const [confirmationInput, setConfirmationInput] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setEmail("");
      setConfirmationInput("");
      setError(null);
    }
  }, [open]);

  const canSubmit = email.trim() !== "" && confirmationInput === confirmation && !transferring;

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setTransferring(true);
    setError(null);
    try {
      await onTransfer?.(email.trim());
      onOpenChange?.(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Transfer failed");
    } finally {
      setTransferring(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Transfer ownership</DialogTitle>
          <DialogDescription>{children}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="transfer-email">New owner&apos;s email</Label>
            <Input
              id="transfer-email"
              type="email"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="transfer-confirmation">
              To confirm, type <span className="font-semibold">{confirmation}</span>
            </Label>
            <Input
              id="transfer-confirmation"
              autoComplete="off"
              value={confirmationInput}
              onChange={(e) => setConfirmationInput(e.target.value)}
            />
          </div>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="submit" variant="destructive" disabled={!canSubmit}>
              {transferring ? "Transferring..." : "Transfer ownership"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
